import {
  Box,
  Image as ChakraImage,
  SimpleGrid,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react';
import Link from 'next/link';

import Image from '@/app/_components/Image';
import LinkCustom from '@/app/_components/link/LinkCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import { ReactComponent as PromotionTag } from '@/app/_icons/promotion-tag_white.svg';
import { ReactComponent as StarTag } from '@/app/_icons/star.svg';
import { getLeadTypeForSpecial } from '@/services/specials/specials.service';
import { Special } from '@/types/specials/specials.types';

import Styles from './PromotionsCard.module.scss';

const PromotionsCard = ({
  item,
  isButtonVisible = true,
  hrefBase = '/promotions',
  logoUrl,
  corporateName,
}: {
  item: Special;
  isButtonVisible?: boolean;
  hrefBase?: string;
  logoUrl?: string;
  corporateName?: string;
}) => {
  const {
    title = '',
    sub_title = '',
    monthly_repayment = 0,
    sale_price_incl_vat = 0,
    cashback_amount = 0,
    cashback = false,
    payment_terms = 0,
    deposit = 0,
    residual = 0,
    interest_rate = 0,
    image = '',
  } = item;

  const leadType = getLeadTypeForSpecial(item);

  const getPriceDisplay = () => {
    if (Number(monthly_repayment) > 0) {
      return {
        price: monthly_repayment,
        suffix: 'p/m',
        prefix: deposit > 0 ? 'From' : 'Zero deposit from',
      };
    } else if (sale_price_incl_vat && Number(sale_price_incl_vat) > 0) {
      return {
        price: sale_price_incl_vat,
        suffix: null,
        prefix: 'From',
      };
    } else if (cashback_amount && Number(cashback_amount) > 0) {
      return {
        price: cashback_amount,
        suffix: null,
        prefix: 'Cash Back',
      };
    }
    return null;
  };

  const priceDisplay = getPriceDisplay();
  const showPriceInfo = priceDisplay !== null;

  return (
    <Box
      className={Styles.promotionsCard}
      display={'flex'}
      flexDirection={'column'}
      data-testid="promotion-card"
      data-promotion-id={item.id}
      position="relative"
    >
      {logoUrl && (
        <ChakraImage
          src={logoUrl}
          alt={corporateName || 'Corporate Logo'}
          boxSize="50px"
          objectFit="contain"
          position="absolute"
          top="10px"
          left="10px"
          bg="white"
          borderRadius="md"
          p={1}
          zIndex={10}
        />
      )}

      {image ? (
        <Link 
          className={Styles.promotionsCardImage}
          href={`${hrefBase}/${item.id}`}
          aria-label={`View ${title} promotion`}
        >
          <Image
            loading="lazy"
            src={`${image}?key=image-600-714`}
            width={320}
            height={476}
            alt={title}
            title={title}
          />
        </Link>
      ) : null}
      <Box
        display={'flex'}
        flexDir={'row'}
        alignItems={'center'}
        justifyContent={'end'}
        className={Styles.promotionsCardHeader}
        gap={2}
        padding={6}
      >
        {cashback ? (
          <Tag
            borderRadius={0}
            variant="solid"
            className={`${Styles.promotionTag} ${Styles.darkTag}`}
          >
            <TagLeftIcon as={StarTag} width={4} height={4} />
            <TagLabel>Cash Back</TagLabel>
          </Tag>
        ) : null}
        {payment_terms || deposit || residual || interest_rate ? (
          <Tag
            borderRadius={0}
            variant="solid"
            className={`${Styles.promotionTag} ${Styles.redTag}`}
          >
            <TagLeftIcon as={PromotionTag} width={4} height={4} />
            <TagLabel>Finance</TagLabel>
          </Tag>
        ) : null}
      </Box>
      <Box
        className={Styles.promotionsCardBody}
        marginTop={'auto'}
        paddingX={[2, 2, 6, 6]}
      >
        <Link
          href={`${hrefBase}/${item.id}`}
          className={Styles.carModelName}
          data-testid="promotion-title"
        >
          {title}
        </Link>
      </Box>
      <Box
        className={Styles.promotionsCardFooter}
        paddingX={[2, 2, 6, 6]}
        paddingBottom={6}
      >
        <Stack
          display={'flex'}
          flexDirection={'row'}
          className={Styles.carModelDetail}
          gap={2}
        >
          <Box className={Styles.carInfo} maxW={'180'} width={'100%'} mt={4}>
            <Text
              data-testid="promotion-description"
              className={Styles.description}
            >
              {sub_title}
            </Text>
          </Box>
          {showPriceInfo && priceDisplay && (
            <Box className={Styles.carPriceInfo} marginLeft={'auto'}>
              <Text className={Styles.carPriceDesc}>{priceDisplay.prefix}</Text>
              <Text className={Styles.carPrice}>
                <PriceFormat prefix="R" value={priceDisplay.price} />
                {priceDisplay.suffix ? ` ${priceDisplay.suffix}` : null}
              </Text>
              <Text className={Styles.carPriceDesc} textAlign={'right'}>
                Inc VAT
              </Text>
            </Box>
          )}
        </Stack>
        {isButtonVisible && (
          <SimpleGrid
            columns={[2, 2, 2, 2]}
            alignItems={'start'}
            spacing={[3]}
            mt={4}
            paddingX={{ base: 0, md: 6 }}
            className={Styles.onHoverCTA}
          >
            <LinkCustom
              className="btnOutlineWhite"
              moduleClass={Styles.btnCTA}
              href={`${hrefBase}/${item.id}`}
              data-testid="explore-button"
            >
              Explore
            </LinkCustom>
            <LinkCustom
              className="btnOutlineWhite"
              moduleClass={Styles.btnCTA}
              href={`${hrefBase}/${item.id}?form=${leadType}`}
              data-testid="enquire-button"
            >
              Enquire now
            </LinkCustom>
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
};

export default PromotionsCard;
